import { useContext, useEffect, useRef, useState } from 'react';
import Card from 'components/card';
import InputField from 'components/fields/InputField';
import companyServices from 'ApiService/Placement/companyServices';
import clientServices from 'ApiService/Placement/clientServices';
import { AuthContext } from 'contexts/AuthContext';
import ConfirmationModal from 'pages/Common/ConfirmationModal';
import toast from 'react-hot-toast';

// The global toast style is single-line and clips long messages on phones.
const WRAPPING_TOAST = {
  duration: 6000,
  style: { whiteSpace: 'normal', height: 'auto', maxWidth: '90vw' },
};

const Index = () => {
  const { state } = useContext(AuthContext);

  // App logic state
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); // 'all' | 'active' | 'deactivated'

  // Company State
  const [selectedCompany, setSelectedCompany] = useState(null);
  const detailsRef = useRef(null);
  const [showCompanyModal, setShowCompanyModal] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);

  // Company activate/deactivate State
  const [deactivateTarget, setDeactivateTarget] = useState(null);
  const [deactivateReason, setDeactivateReason] = useState('');
  const [activateTarget, setActivateTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [statusSubmitting, setStatusSubmitting] = useState(false);
  const [companyFormData, setCompanyFormData] = useState({
    name: '',
    hasClients: false,
    industry: '',
    location: '',
    website: '',
    contactPerson: '',
    contactEmail: '',
  });

  // Client State
  const [clients, setClients] = useState([]);
  const [clientsLoading, setClientsLoading] = useState(false);
  const [showClientModal, setShowClientModal] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [clientFormData, setClientFormData] = useState({
    clientName: '',
    location: '',
    contactPerson: '',
    contactEmail: '',
  });

  // Fetch all companies
  const fetchCompanies = async () => {
    setLoading(true);
    try {
      const response = await companyServices.getAllCompanies();
      if (response.data.success) {
        setCompanies(response.data.companies);
        // If a company is already selected, update it with fresh data
        if (selectedCompany) {
          const updatedCompany = response.data.companies.find(
            (c) => c._id === selectedCompany._id
          );
          if (updatedCompany) setSelectedCompany(updatedCompany);
          else {
            setSelectedCompany(null);
            setClients([]);
          }
        }
      } else {
        toast.error('Failed to fetch companies');
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.message || 'Failed to fetch companies'
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCompanies();
  }, []);

  // Fetch clients for a specific company
  const fetchClients = async (companyId) => {
    setClientsLoading(true);
    try {
      const response = await clientServices.getClientsByCompany(companyId);
      if (response.data.success) {
        setClients(response.data.clients);
      } else {
        toast.error('Failed to fetch clients');
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to fetch clients');
    } finally {
      setClientsLoading(false);
    }
  };

  // The layout scrolls an inner container, not the window; reset every
  // scrolled ancestor so the details aren't hidden under the sticky navbar.
  const scrollPageToTop = () => {
    for (let el = detailsRef.current; el; el = el.parentElement) {
      if (el.scrollTop > 0) el.scrollTop = 0;
    }
    window.scrollTo(0, 0);
  };

  const handleCompanySelect = async (company) => {
    setSelectedCompany(company);
    // Below lg the list is hidden while a company is open, so the details
    // start at the top of the page; the list may have been scrolled far down.
    if (window.matchMedia('(max-width: 1023px)').matches) {
      requestAnimationFrame(scrollPageToTop);
    }
    if (company.hasClients) {
      await fetchClients(company._id);
    } else {
      setClients([]);
    }
  };

  const statusCounts = {
    all: companies.length,
    active: companies.filter((c) => c.isActive).length,
    deactivated: companies.filter((c) => !c.isActive).length,
  };

  const filteredCompanies = companies
    .filter((c) => {
      if (statusFilter === 'active') return c.isActive;
      if (statusFilter === 'deactivated') return !c.isActive;
      return true;
    })
    .filter(
      (c) =>
        c.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (c.industry &&
          c.industry.toLowerCase().includes(searchQuery.toLowerCase()))
    );

  // --- Company Form Handlers ---
  const handleCompanyChange = (e) => {
    const { name, value, type, checked } = e.target;
    setCompanyFormData({
      ...companyFormData,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  const handleCompanySubmit = async (e) => {
    e.preventDefault();
    try {
      let response;
      if (editingCompany) {
        response = await companyServices.updateCompany(
          editingCompany._id,
          companyFormData
        );
      } else {
        response = await companyServices.createCompany(companyFormData);
      }

      if (response.data.success) {
        toast.success(response.data.message);
        setShowCompanyModal(false);
        resetCompanyForm();
        fetchCompanies();
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to save company');
    }
  };

  const handleCompanyEdit = (company) => {
    setEditingCompany(company);
    setCompanyFormData({
      name: company.name || '',
      hasClients: company.hasClients || false,
      industry: company.industry || '',
      location: company.location || '',
      website: company.website || '',
      contactPerson: company.contactPerson || '',
      contactEmail: company.contactEmail || '',
    });
    setShowCompanyModal(true);
  };

  const handleCompanyToggleStatus = (company) => {
    if (company.isActive) {
      setDeactivateReason('');
      setDeactivateTarget(company);
    } else {
      setActivateTarget(company);
    }
  };

  const submitDeactivate = async () => {
    if (!deactivateTarget) return;
    setStatusSubmitting(true);
    try {
      const response = await companyServices.toggleCompanyStatus(
        deactivateTarget._id,
        { reason: deactivateReason.trim() }
      );
      if (response.data.success) {
        toast.success(response.data.message);
        setDeactivateTarget(null);
        setDeactivateReason('');
        fetchCompanies();
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to update status');
    } finally {
      setStatusSubmitting(false);
    }
  };

  const submitActivate = async () => {
    if (!activateTarget) return;
    setStatusSubmitting(true);
    try {
      const response = await companyServices.toggleCompanyStatus(
        activateTarget._id
      );
      if (response.data.success) {
        toast.success(response.data.message);
        setActivateTarget(null);
        fetchCompanies();
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to update status');
    } finally {
      setStatusSubmitting(false);
    }
  };

  const submitDelete = async () => {
    if (!deleteTarget) return;
    setStatusSubmitting(true);
    try {
      const response = await companyServices.deleteCompany(deleteTarget._id);
      if (response.data.success) {
        toast.success(response.data.message);
        if (selectedCompany?._id === deleteTarget._id) {
          setSelectedCompany(null);
        }
        setDeleteTarget(null);
        fetchCompanies();
      } else {
        toast.error(response.data.message, WRAPPING_TOAST);
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.message || 'Failed to delete company',
        WRAPPING_TOAST
      );
      setDeleteTarget(null);
    } finally {
      setStatusSubmitting(false);
    }
  };

  const resetCompanyForm = () => {
    setCompanyFormData({
      name: '',
      hasClients: false,
      industry: '',
      location: '',
      website: '',
      contactPerson: '',
      contactEmail: '',
    });
    setEditingCompany(null);
  };

  // --- Client Form Handlers ---
  const handleClientChange = (e) => {
    const { name, value } = e.target;
    setClientFormData({ ...clientFormData, [name]: value });
  };

  const handleClientSubmit = async (e) => {
    e.preventDefault();
    if (!selectedCompany) {
      toast.error('Please select a company first');
      return;
    }

    try {
      const clientData = {
        ...clientFormData,
        companyId: selectedCompany._id,
      };

      let response;
      if (editingClient) {
        response = await clientServices.updateClient(
          editingClient._id,
          clientData
        );
      } else {
        response = await clientServices.createClient(clientData);
      }

      if (response.data.success) {
        toast.success(response.data.message);
        setShowClientModal(false);
        resetClientForm();
        fetchClients(selectedCompany._id);
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to save client');
    }
  };

  const handleClientEdit = (client) => {
    setEditingClient(client);
    setClientFormData({
      clientName: client.clientName || '',
      location: client.location || '',
      contactPerson: client.contactPerson || '',
      contactEmail: client.contactEmail || '',
    });
    setShowClientModal(true);
  };

  const handleClientToggleStatus = async (client) => {
    if (
      !window.confirm(
        `Are you sure you want to ${
          client.isActive ? 'deactivate' : 'activate'
        } ${client.clientName}?`
      )
    )
      return;
    try {
      const response = await clientServices.toggleClientStatus(client._id);
      if (response.data.success) {
        toast.success(response.data.message);
        if (selectedCompany) fetchClients(selectedCompany._id);
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to update status');
    }
  };

  const resetClientForm = () => {
    setClientFormData({
      clientName: '',
      location: '',
      contactPerson: '',
      contactEmail: '',
    });
    setEditingClient(null);
  };

  return (
    <div className="mt-4 h-full w-full">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-navy-700 dark:text-white">
          Company & Client Management
        </h2>
        <p className="text-gray-600 dark:text-gray-400">
          Manage your placement partner companies and their clients from a
          single space.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:h-[calc(100vh-180px)] lg:min-h-[600px] lg:grid-cols-3 xl:grid-cols-4">
        {/* Left Column: Companies List */}
        <Card
          extra={`p-4 flex-col col-span-1 h-full shadow-md ${
            selectedCompany ? 'hidden lg:flex' : 'flex'
          }`}
        >
          <div className="mb-4 flex items-center justify-between">
            <h3 className="text-lg font-bold text-navy-700 dark:text-white">
              Companies
            </h3>
            <button
              onClick={() => {
                resetCompanyForm();
                setShowCompanyModal(true);
              }}
              className="rounded-lg bg-brand-500 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-600"
            >
              + Add
            </button>
          </div>
          <div className="mb-3">
            <input
              type="text"
              placeholder="Search companies..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:border-brand-500 dark:border-navy-600 dark:bg-navy-700 dark:text-white"
            />
          </div>

          <div className="mb-4 flex gap-1 rounded-lg bg-gray-100 p-1 dark:bg-navy-700">
            {[
              { key: 'all', label: 'All' },
              { key: 'active', label: 'Active' },
              { key: 'deactivated', label: 'Deactivated' },
            ].map((tab) => (
              <button
                key={tab.key}
                onClick={() => setStatusFilter(tab.key)}
                className={`flex-1 rounded-md px-2 py-1.5 text-xs font-medium transition-colors ${
                  statusFilter === tab.key
                    ? 'bg-white text-brand-600 shadow-sm dark:bg-navy-600 dark:text-white'
                    : 'text-gray-500 hover:text-navy-700 dark:hover:text-white'
                }`}
              >
                {tab.label} ({statusCounts[tab.key]})
              </button>
            ))}
          </div>

          {loading ? (
            <div className="flex flex-1 items-center justify-center">
              <p className="text-sm text-gray-500">Loading...</p>
            </div>
          ) : (
            <div className="custom-scrollbar flex-1 space-y-2 overflow-y-auto pr-1">
              {filteredCompanies.length === 0 ? (
                <p className="py-4 text-center text-sm text-gray-500">
                  No companies found.
                </p>
              ) : (
                filteredCompanies.map((c) => (
                  <button
                    key={c._id}
                    onClick={() => handleCompanySelect(c)}
                    className={`w-full rounded-xl border p-3 text-left transition-all duration-200 ${
                      selectedCompany?._id === c._id
                        ? 'dark:bg-brand-900/20 border-brand-500 bg-brand-50 shadow-sm'
                        : 'border-gray-100 bg-white hover:border-gray-300 hover:bg-gray-50 dark:border-navy-600 dark:bg-navy-700 dark:hover:bg-navy-600'
                    }`}
                  >
                    <div className="mb-1 flex items-start justify-between">
                      <span className="truncate pr-2 font-semibold text-navy-700 dark:text-white">
                        {c.name}
                      </span>
                      <span
                        className={`flex-shrink-0 rounded-full px-2 py-0.5 text-[10px] font-medium ${
                          c.isActive
                            ? 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300'
                            : 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300'
                        }`}
                      >
                        {c.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-xs">
                      <span className="truncate text-gray-500">
                        {c.industry || 'No Industry'}
                      </span>
                      {c.hasClients && (
                        <span className="flex-shrink-0 rounded bg-blue-100 px-1.5 py-0.5 text-[10px] font-medium text-blue-700 dark:bg-blue-900 dark:text-blue-300">
                          Clients
                        </span>
                      )}
                    </div>
                  </button>
                ))
              )}
            </div>
          )}
        </Card>

        {/* Right Column: Details & Clients */}
        <div
          ref={detailsRef}
          className={`col-span-1 h-full flex-col overflow-y-auto pr-1 lg:col-span-2 lg:flex xl:col-span-3 ${
            selectedCompany ? 'flex' : 'hidden'
          }`}
        >
          {!selectedCompany ? (
            <div className="flex flex-1 flex-col items-center justify-center rounded-2xl border-2 border-dashed border-gray-200 bg-white dark:border-navy-600 dark:bg-navy-800">
              <div className="mb-4 text-5xl text-gray-400 opacity-50">🏢</div>
              <h3 className="text-xl font-bold text-navy-700 dark:text-white">
                No Company Selected
              </h3>
              <p className="mt-2 text-gray-500">
                Select a company from the list to view its details and manage
                clients.
              </p>
              <button
                onClick={() => {
                  resetCompanyForm();
                  setShowCompanyModal(true);
                }}
                className="mt-6 rounded-lg bg-brand-500 px-5 py-2 text-sm font-medium text-white hover:bg-brand-600"
              >
                Create New Company
              </button>
            </div>
          ) : (
            <div className="space-y-6">
              <button
                onClick={() => setSelectedCompany(null)}
                className="text-sm font-medium text-brand-500 hover:text-brand-600 lg:hidden"
              >
                ← Back to companies
              </button>

              {/* Company Detail Card */}
              <Card extra="p-6">
                <div className="flex flex-col items-start justify-between gap-4 border-b border-gray-100 pb-4 dark:border-navy-600 md:flex-row md:items-center">
                  <div>
                    <div className="flex items-center gap-3">
                      <h2 className="text-2xl font-bold text-navy-700 dark:text-white">
                        {selectedCompany.name}
                      </h2>
                      <span
                        className={`rounded-full px-2 py-1 text-xs font-medium ${
                          selectedCompany.isActive
                            ? 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-200'
                            : 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-200'
                        }`}
                      >
                        {selectedCompany.isActive ? 'Active' : 'Deactivated'}
                      </span>
                    </div>
                    <p className="mt-1 text-sm font-medium text-brand-500">
                      {selectedCompany.industry || 'No Industry Defined'}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleCompanyEdit(selectedCompany)}
                      className="rounded-lg bg-gray-100 px-4 py-2 text-sm font-medium text-navy-700 transition-colors hover:bg-gray-200 dark:bg-navy-600 dark:text-white dark:hover:bg-navy-500"
                    >
                      Edit Details
                    </button>
                    <button
                      onClick={() => handleCompanyToggleStatus(selectedCompany)}
                      className={`rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
                        selectedCompany.isActive
                          ? 'bg-red-50 text-red-600 hover:bg-red-100 dark:bg-red-900/30 dark:hover:bg-red-900/50'
                          : 'bg-green-50 text-green-600 hover:bg-green-100 dark:bg-green-900/30 dark:hover:bg-green-900/50'
                      }`}
                    >
                      {selectedCompany.isActive ? 'Deactivate' : 'Activate'}
                    </button>
                    <button
                      onClick={() => setDeleteTarget(selectedCompany)}
                      className="rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-red-700"
                    >
                      Delete
                    </button>
                  </div>
                </div>

                {!selectedCompany.isActive && (
                  <div className="mt-4 rounded-xl border border-red-200 bg-red-50 p-4 dark:border-red-900/50 dark:bg-red-900/20">
                    <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-red-600 dark:text-red-300">
                      ⚠️ Deactivation reason
                    </p>
                    <p className="mt-1 whitespace-pre-wrap text-sm text-navy-700 dark:text-white">
                      {selectedCompany.deactivationReason || 'No reason provided'}
                    </p>
                    <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                      {selectedCompany.deactivatedAt
                        ? `Deactivated on ${new Date(
                            selectedCompany.deactivatedAt
                          ).toLocaleString()}`
                        : 'Deactivated'}
                      {selectedCompany.deactivatedBy?.name
                        ? ` · by ${selectedCompany.deactivatedBy.name}`
                        : ''}
                    </p>
                  </div>
                )}

                <div className="grid grid-cols-1 gap-6 pt-5 md:grid-cols-2 lg:grid-cols-3">
                  <div>
                    <p className="mb-1 text-xs uppercase tracking-wider text-gray-500">
                      Location
                    </p>
                    <p className="flex items-center gap-1.5 text-sm font-medium text-navy-700 dark:text-white">
                      📍 {selectedCompany.location || 'N/A'}
                    </p>
                  </div>
                  <div>
                    <p className="mb-1 text-xs uppercase tracking-wider text-gray-500">
                      Website
                    </p>
                    <p className="flex items-center gap-1.5 text-sm font-medium text-navy-700 dark:text-white">
                      🌐{' '}
                      {selectedCompany.website ? (
                        <a
                          href={selectedCompany.website}
                          target="_blank"
                          rel="noreferrer"
                          className="text-brand-500 hover:underline"
                        >
                          {selectedCompany.website}
                        </a>
                      ) : (
                        'N/A'
                      )}
                    </p>
                  </div>
                  <div>
                    <p className="mb-1 text-xs uppercase tracking-wider text-gray-500">
                      Company Type
                    </p>
                    <p className="flex items-center gap-1.5 text-sm font-medium text-navy-700 dark:text-white">
                      📋{' '}
                      {selectedCompany.hasClients
                        ? 'Staffing / Has Clients'
                        : 'Direct Employer'}
                    </p>
                  </div>
                  <div>
                    <p className="mb-1 text-xs uppercase tracking-wider text-gray-500">
                      Contact Person
                    </p>
                    <p className="flex items-center gap-1.5 text-sm font-medium text-navy-700 dark:text-white">
                      👤 {selectedCompany.contactPerson || 'N/A'}
                    </p>
                  </div>
                  <div>
                    <p className="mb-1 text-xs uppercase tracking-wider text-gray-500">
                      Contact Email
                    </p>
                    <p className="flex items-center gap-1.5 text-sm font-medium text-navy-700 dark:text-white">
                      ✉️ {selectedCompany.contactEmail || 'N/A'}
                    </p>
                  </div>
                </div>
              </Card>

              {/* Clients Section (Only shown if hasClients is true) */}
              {selectedCompany.hasClients && (
                <div className="mt-6">
                  <div className="mb-4 flex items-end justify-between gap-3 px-1">
                    <div className="min-w-0">
                      <h3 className="flex items-center gap-2 text-xl font-bold text-navy-700 dark:text-white">
                        Clients
                        <span className="rounded-full bg-brand-100 px-2 py-0.5 text-xs text-brand-700 dark:bg-brand-900 dark:text-brand-300">
                          {clients.length}
                        </span>
                      </h3>
                      <p className="mt-1 text-sm text-gray-500">
                        Manage external clients associated with{' '}
                        {selectedCompany.name}
                      </p>
                    </div>
                    <button
                      onClick={() => {
                        resetClientForm();
                        setShowClientModal(true);
                      }}
                      className="shrink-0 whitespace-nowrap rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-brand-600"
                    >
                      + Add Client
                    </button>
                  </div>

                  {clientsLoading ? (
                    <Card extra="p-10 flex items-center justify-center border-dashed border-2 shadow-none">
                      <p className="text-gray-500">Loading clients...</p>
                    </Card>
                  ) : clients.length === 0 ? (
                    <Card extra="p-10 flex flex-col items-center justify-center border-dashed border-2 border-gray-200 shadow-none dark:border-navy-600 bg-gray-50/50 dark:bg-navy-800/50">
                      <div className="mb-3 text-4xl opacity-30">🏢</div>
                      <p className="font-medium text-gray-500">
                        No clients added yet for this company.
                      </p>
                      <button
                        onClick={() => {
                          resetClientForm();
                          setShowClientModal(true);
                        }}
                        className="mt-3 text-sm font-medium text-brand-500 hover:text-brand-600"
                      >
                        Add your first client
                      </button>
                    </Card>
                  ) : (
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3">
                      {clients.map((client) => (
                        <Card
                          key={client._id}
                          extra="p-5 hover:shadow-lg transition-shadow border border-gray-100 dark:border-navy-600 shadow-sm"
                        >
                          <div className="mb-3 flex items-start justify-between">
                            <h4
                              className="line-clamp-1 pr-2 text-base font-bold text-navy-700 dark:text-white"
                              title={client.clientName}
                            >
                              {client.clientName}
                            </h4>
                            <span
                              className={`flex-shrink-0 rounded-full px-2 py-0.5 text-[10px] font-medium ${
                                client.isActive
                                  ? 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300'
                                  : 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300'
                              }`}
                            >
                              {client.isActive ? 'Active' : 'Inactive'}
                            </span>
                          </div>

                          <div className="mb-5 space-y-2">
                            <p className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400">
                              <span className="opacity-70">📍</span>{' '}
                              {client.location || 'No location'}
                            </p>
                            {(client.contactPerson || client.contactEmail) && (
                              <div className="border-t border-gray-50 pt-1 text-xs text-gray-600 dark:border-navy-600 dark:text-gray-400">
                                {client.contactPerson && (
                                  <p className="mt-1 flex items-center gap-2">
                                    <span className="opacity-70">👤</span>{' '}
                                    {client.contactPerson}
                                  </p>
                                )}
                                {client.contactEmail && (
                                  <p
                                    className="mt-1 flex items-center gap-2 truncate"
                                    title={client.contactEmail}
                                  >
                                    <span className="opacity-70">✉️</span>{' '}
                                    {client.contactEmail}
                                  </p>
                                )}
                              </div>
                            )}
                          </div>

                          <div className="mt-auto flex gap-2 border-t border-gray-100 pt-3 dark:border-navy-600">
                            <button
                              onClick={() => handleClientEdit(client)}
                              className="flex-1 rounded bg-gray-50 py-1.5 text-xs font-semibold text-navy-700 transition-colors hover:bg-gray-200 dark:bg-navy-600 dark:text-white dark:hover:bg-navy-500"
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => handleClientToggleStatus(client)}
                              className={`flex-1 rounded py-1.5 text-xs font-semibold transition-colors ${
                                client.isActive
                                  ? 'bg-red-50 text-red-600 hover:bg-red-100 dark:bg-red-900/20 dark:hover:bg-red-900/40'
                                  : 'bg-green-50 text-green-600 hover:bg-green-100 dark:bg-green-900/20 dark:hover:bg-green-900/40'
                              }`}
                            >
                              {client.isActive ? 'Deactivate' : 'Activate'}
                            </button>
                          </div>
                        </Card>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* --- Modals --- */}
      {/* Deactivate Company Modal */}
      {deactivateTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-navy-900/60 p-4 backdrop-blur-sm">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl dark:bg-navy-800">
            <h3 className="text-lg font-bold text-navy-700 dark:text-white">
              Deactivate {deactivateTarget.name}
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              This company will be hidden from job posting flows. You can
              reactivate it any time.
            </p>
            <label className="mt-4 block text-sm font-medium text-navy-700 dark:text-white">
              Reason for deactivation (optional)
            </label>
            <textarea
              rows={4}
              value={deactivateReason}
              onChange={(e) => setDeactivateReason(e.target.value)}
              placeholder="e.g., No active requirements this quarter"
              className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:border-brand-500 dark:border-navy-600 dark:bg-navy-700 dark:text-white"
            />
            <div className="mt-6 flex gap-3">
              <button
                type="button"
                onClick={() => {
                  setDeactivateTarget(null);
                  setDeactivateReason('');
                }}
                disabled={statusSubmitting}
                className="flex-1 rounded-xl bg-gray-100 px-4 py-2.5 font-semibold text-gray-700 hover:bg-gray-200 disabled:opacity-60 dark:bg-navy-600 dark:text-gray-200 dark:hover:bg-navy-500"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={submitDeactivate}
                disabled={statusSubmitting}
                className="flex-1 rounded-xl bg-red-500 px-4 py-2.5 font-semibold text-white shadow-md hover:bg-red-600 disabled:opacity-60"
              >
                {statusSubmitting ? 'Deactivating...' : 'Deactivate'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Activate Company Modal */}
      <ConfirmationModal
        isOpen={!!activateTarget}
        icon="warning"
        title="Activate company"
        message={
          activateTarget
            ? `Reactivate ${activateTarget.name}? It will become available for job posting again.`
            : ''
        }
        confirmText="Activate"
        onConfirm={submitActivate}
        onClose={() => setActivateTarget(null)}
        disableConfirm={statusSubmitting}
      />

      {/* Delete Company Modal */}
      <ConfirmationModal
        isOpen={!!deleteTarget}
        icon="warning"
        title="Delete company"
        message={
          deleteTarget
            ? `Permanently delete ${deleteTarget.name}? This can't be undone. If it is already in use, deactivate it instead.`
            : ''
        }
        confirmText="Delete"
        onConfirm={submitDelete}
        onClose={() => setDeleteTarget(null)}
        disableConfirm={statusSubmitting}
      />

      {/* Company Modal */}
      {showCompanyModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-navy-900/60 p-4 backdrop-blur-sm">
          <div className="max-h-[90vh] w-full max-w-lg overflow-auto rounded-2xl bg-white p-6 shadow-xl dark:bg-navy-800">
            <h3 className="mb-5 border-b pb-3 text-xl font-bold text-navy-700 dark:border-navy-600 dark:text-white">
              {editingCompany
                ? 'Edit Company Configuration'
                : 'Create New Company'}
            </h3>
            <form onSubmit={handleCompanySubmit} className="space-y-4">
              <InputField
                label="Company Name *"
                name="name"
                value={companyFormData.name}
                onChange={handleCompanyChange}
                placeholder="e.g., TCS, Infosys, Google"
                required
              />

              <div className="flex items-center gap-3 rounded-xl border border-gray-100 bg-gray-50 p-3 dark:border-navy-600 dark:bg-navy-700">
                <input
                  type="checkbox"
                  name="hasClients"
                  id="hasClients"
                  checked={companyFormData.hasClients}
                  onChange={handleCompanyChange}
                  className="h-5 w-5 rounded border-gray-300 text-brand-500 focus:ring-brand-500"
                />
                <label
                  htmlFor="hasClients"
                  className="cursor-pointer select-none text-sm font-semibold text-navy-700 dark:text-white"
                >
                  This company has associated clients
                  <p className="mt-0.5 text-xs font-normal text-gray-500">
                    Check this if the company acts as a staffing or placement
                    agency
                  </p>
                </label>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <InputField
                  label="Industry"
                  name="industry"
                  value={companyFormData.industry}
                  onChange={handleCompanyChange}
                  placeholder="e.g., IT, Finance"
                />
                <InputField
                  label="Location"
                  name="location"
                  value={companyFormData.location}
                  onChange={handleCompanyChange}
                  placeholder="e.g., Bangalore"
                />
              </div>

              <InputField
                label="Website"
                name="website"
                value={companyFormData.website}
                onChange={handleCompanyChange}
                placeholder="https://example.com"
              />

              <div className="grid grid-cols-2 gap-4">
                <InputField
                  label="Contact Person"
                  name="contactPerson"
                  value={companyFormData.contactPerson}
                  onChange={handleCompanyChange}
                  placeholder="e.g., John Doe"
                />
                <InputField
                  label="Contact Email"
                  name="contactEmail"
                  type="email"
                  value={companyFormData.contactEmail}
                  onChange={handleCompanyChange}
                  placeholder="email@company.com"
                />
              </div>

              <div className="mt-6 flex gap-3 border-t pt-5 dark:border-navy-600">
                <button
                  type="button"
                  onClick={() => {
                    setShowCompanyModal(false);
                    resetCompanyForm();
                  }}
                  className="flex-1 rounded-xl bg-gray-100 px-4 py-2.5 font-semibold text-gray-700 hover:bg-gray-200 dark:bg-navy-600 dark:text-gray-200 dark:hover:bg-navy-500"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="shadow-brand-500/30 flex-1 rounded-xl bg-brand-500 px-4 py-2.5 font-semibold text-white shadow-md hover:bg-brand-600"
                >
                  {editingCompany ? 'Save Changes' : 'Create Company'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Client Modal */}
      {showClientModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-navy-900/60 p-4 backdrop-blur-sm">
          <div className="max-h-[90vh] w-full max-w-lg overflow-auto rounded-2xl bg-white p-6 shadow-xl dark:bg-navy-800">
            <h3 className="mb-5 border-b pb-3 text-xl font-bold text-navy-700 dark:border-navy-600 dark:text-white">
              {editingClient
                ? 'Edit Client Details'
                : `Add Client for ${selectedCompany?.name}`}
            </h3>
            <form onSubmit={handleClientSubmit} className="space-y-4">
              <InputField
                label="Client Name *"
                name="clientName"
                value={clientFormData.clientName}
                onChange={handleClientChange}
                placeholder="e.g., TCS Mumbai, Google Tech"
                required
              />
              <InputField
                label="Location"
                name="location"
                value={clientFormData.location}
                onChange={handleClientChange}
                placeholder="e.g., Mumbai, Bangalore"
              />
              <div className="grid grid-cols-2 gap-4">
                <InputField
                  label="Contact Person"
                  name="contactPerson"
                  value={clientFormData.contactPerson}
                  onChange={handleClientChange}
                  placeholder="e.g., John Doe"
                />
                <InputField
                  label="Contact Email"
                  name="contactEmail"
                  type="email"
                  value={clientFormData.contactEmail}
                  onChange={handleClientChange}
                  placeholder="contact@client.com"
                />
              </div>

              <div className="mt-6 flex gap-3 border-t pt-5 dark:border-navy-600">
                <button
                  type="button"
                  onClick={() => {
                    setShowClientModal(false);
                    resetClientForm();
                  }}
                  className="flex-1 rounded-xl bg-gray-100 px-4 py-2.5 font-semibold text-gray-700 hover:bg-gray-200 dark:bg-navy-600 dark:text-gray-200 dark:hover:bg-navy-500"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="shadow-brand-500/30 flex-1 rounded-xl bg-brand-500 px-4 py-2.5 font-semibold text-white shadow-md hover:bg-brand-600"
                >
                  {editingClient ? 'Save Changes' : 'Create Client'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Index;
